import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { listPousadas, getPousada } from "@/lib/publicApi";
import { PousadaCard } from "@/components/public/PousadaCard";
import { BRAND } from "@/lib/brand";

export default function PousadasList() {
  const [pousadas, setPousadas] = useState([]);
  const [precos, setPrecos] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const list = await listPousadas();
        if (cancel) return;
        setPousadas(list);
        // Busca preços em paralelo (não bloqueante do render do card).
        const results = await Promise.all(
          list.map((p) => getPousada(p.slug || p.id).catch(() => null))
        );
        if (cancel) return;
        const map = {};
        results.forEach((d) => {
          if (!d || !Array.isArray(d.quartos)) return;
          const vals = d.quartos.map((q) => Number(q.valor_diaria || 0)).filter((v) => v > 0);
          if (vals.length) map[d.id] = Math.min(...vals);
        });
        setPrecos(map);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  return (
    <>
      <Helmet>
        <title>{`Pousadas · ${BRAND.name}`}</title>
        <meta name="description" content={`Conheça todas as pousadas da ${BRAND.name}. Reserve diretamente com quem opera.`} />
        <link rel="canonical" href="/pousadas" />
        <meta property="og:title" content={`Pousadas · ${BRAND.name}`} />
        <meta property="og:description" content={`Conheça todas as pousadas da ${BRAND.name}.`} />
      </Helmet>

      <section className="bg-[#F5F1E8] border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-14 lg:py-20">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4A652] font-semibold mb-3">
            Nossa rede
          </div>
          <h1 className="font-public-title text-4xl lg:text-5xl text-stone-900 leading-[1.05] max-w-3xl">
            Todas as pousadas da {BRAND.name}
          </h1>
          <p className="mt-5 text-stone-600 text-base max-w-2xl leading-relaxed">
            Endereços operados por nós mesmos, com atendimento humano e preço real.
          </p>
        </div>
      </section>

      <section data-testid="pousadas-list" className="max-w-7xl mx-auto px-5 lg:px-10 py-14 lg:py-16">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="aspect-[4/3] rounded-lg bg-stone-200/60 animate-pulse" />
            ))}
          </div>
        ) : pousadas.length === 0 ? (
          <div className="text-center py-20 text-stone-500 text-sm">
            Nenhuma pousada publicada no momento.
          </div>
        ) : (
          <>
            <div className="text-xs text-stone-500 mb-6">
              {pousadas.length} {pousadas.length === 1 ? "pousada" : "pousadas"}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pousadas.map((p) => (
                <PousadaCard key={p.id} pousada={p} precoDesde={precos[p.id]} />
              ))}
            </div>
          </>
        )}
      </section>
    </>
  );
}
